<template>
  <div>
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableRemote
      v-else
      ref="tableRemoteRef"
      :iconHeader="hasPermission('coupons.add') ? 'add' : null"
      :items="displayedCoupons"
      :headers="tableHeaders"
      :pagination="pagination"
      :itemActions="itemActions"
      :title="$t('coupons.coupons')"
      :centerColumns="['action']"
      :loading="loading"
      :isSearchable="false"
      :isInfinitScroll="true"
      :elementsPerPage="queryParams.perpage"
      :notSortableColumns="['action']"
      :sortableColumns="['name', 'active']"
      isCustomIcon
      hideDefaultFooter
      @iconAction="onClickAction"
      @sendQueryParams="fetchQuery"
    >
      <template #item.active="{ item }">
        <v-icon v-if="item.active">
          check
        </v-icon>
      </template>
      <template #SearchWithFilters>
        <SearchWithFilters
          :filters="[]"
          :withoutFilter="true"
          :filterName="filterName"
          @onSearch="handleSearch"
        />
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </VTableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import perpage from '@/constants/pagination';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import { isEmpty } from 'lodash';
import CouponForm from './CouponForm.vue';


export default {
    components: {
        VTableRemote,
        SearchWithFilters,
    },
    data() {
        return {
            coupons: [],
            loading: false,
            pagination: {},
            queryParams: { perPage: perpage },
            filterName: 'coupons',
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'teamMainLang',
        ]),
        tableHeaders() {
            return [
                {
                    text: this.$t('forms.labels.name'),
                    value: 'name',
                    sortable: true,
                },
                {
                    text: this.$t('forms.labels.active'),
                    value: 'active',
                    width: '10%',
                    sortable: true,
                },
            ];
        },
        displayedCoupons() {
            return this.coupons.map(coupon => ({
                id: coupon.id,
                active: coupon.active,
                name: coupon.name[this.userLang] || coupon.name[this.teamMainLang],
            }));
        },
        itemActions() {
            const actions = [];
            if (this.hasPermission('coupons.edit')) {
                actions.push({ name: this.$t('forms.btn.edit'), icon: 'edit', mode: 'edit' });
            }
            return actions;
        },
    },
    async created() {
        await this.getCoupons();
    },
    methods: {
        async getCoupons() {
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);

                const { data } = await this.$rq.getCoupons(cleanQueryParams);
                this.coupons = [...this.coupons, ...data.coupons];
                this.pagination = data.pagination;
            }
        },

        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('coupons.add'),
                showLangs: true,
                component: Vue.component('CouponForm', CouponForm),
                onConfirm: requestPayload => this.addCoupon(requestPayload),
            });
        },

        async fetchQuery(queryParams) {
            this.coupons = [];
            this.pagination = {};
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                page: 1,
                order: queryParams.order,
            };

            await this.getCoupons();
        },
        handleSearch(search) {
            this.fetchQuery({ search });
        },

        async onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
            } else if (action.mode === 'edit') {
                const { data } = await this.$rq.getCoupon(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('coupons.update'),
                    showLangs: true,
                    component: Vue.component('CouponForm', CouponForm),
                    payload: {
                        coupon: data,
                        isEdition: true,
                    },
                    onConfirm: requestPayload => this.updateCoupon(requestPayload),
                });
            }
        },
        async addCoupon(requestPayload) {
            const clone = { ...requestPayload };
            if (!clone?.active) clone.active = true;

            await this.$rq.addCoupon(clone);
            this.$toasted.global.toast_success({
                message: this.$t('coupons.added', { name: requestPayload.name[this.userLang] }),
            });
            await this.fetchQuery({});
        },
        async updateCoupon(requestPayload) {
            if (requestPayload) {
                await this.$rq.updateCoupon(requestPayload.id, requestPayload);
                this.$toasted.global.toast_success({
                    message: this.$t('analyses.types.updated', { name: requestPayload.name[this.userLang] }),
                });
                await this.fetchQuery({});
            }
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getCoupons();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
